import * as React from 'react';
import styled from 'styled-components';
import { ContentSpacing } from '@estee/elc-base-theme';
import { observer } from 'mobx-react';
import { Button } from '@estee/elc-buttons';
import { configConsumer, translate } from '@estee/elc-service';

import { IVtoFeature } from '../../interfaces/IVto';
import { classes } from '../../utils/Classes';
import {
    VTO_ALL_SHADES_BUTTON,
    VTO_PAIR_PRODUCT_LINK,
    VTO_PERFECT_SHADES_BUTTON
} from '../../constants/DataTestIdAttributes';
import { VtoSaveMatch } from '../vto-save-match/VtoSaveMatch';
import { foundationTabs } from '../../constants/Vto';
import { hasPairProduct, hasSaveMatch, hasVtoFoundationFeature } from '~utils/VtoFeatures';

interface IVtoShadeInfoRightContainer {
    isPerfectShadeEnabled?: boolean;
    isAllShadesTabActive?: boolean;
    togglePerfectShades?(hidden: boolean): void;
    setActiveTab(activeTab: string): void;
    saveMatches(): void;
    removeMatches(): void;
    translations: {
        seeAllShadesLabel: string;
        seeBestShadesLabel: string;
        seePairDetailsLabel: string;
    };
    isPairShadesTabActive: boolean;
    pairProductUrl: string;
    isMatchSaved: boolean;
    isSaveInProgress: boolean;
    config: {
        vtoFeature: IVtoFeature;
    };
}

interface IVtoShadesButton extends React.HTMLAttributes<HTMLButtonElement> {
    isPerfectShadeEnabled?: boolean;
    showPairProduct: boolean;
    label: string;
    onClick(event: React.MouseEvent<HTMLButtonElement>): void;
}

export const VtoShadesButton = observer(
    ({ isPerfectShadeEnabled, showPairProduct, label, ...props }: IVtoShadesButton) => {
        if (isPerfectShadeEnabled || showPairProduct) {
            return null;
        }

        return <Button {...props}>{label}</Button>;
    }
);

const Wrapper = styled.div`
    float: right;
    width: 48%;
    padding-right: ${ContentSpacing.space10};
    [dir='rtl'] & {
        float: left;
        padding-right: 0;
        padding-left: ${ContentSpacing.space10};
    }
`;

const shadeInfoRightContainerClassNames = classes('vto-shade-info-right-container');
const vtoPairProductLinkClassName = classes('vto-see-pair-product');
const seeAllShadesClassName = classes('vto-see-all-shades');
const seePerfectShadesClassName = classes('vto-see-perfect-shades');

export const VtoShadeInfoRightContainer = configConsumer(['vtoFeature'])(
    translate(['seeAllShadesLabel', 'seeBestShadesLabel', 'seePairDetailsLabel'])(
        observer(
            ({
                isPerfectShadeEnabled,
                translations,
                togglePerfectShades,
                setActiveTab,
                saveMatches,
                removeMatches,
                isPairShadesTabActive,
                isAllShadesTabActive,
                pairProductUrl,
                isMatchSaved,
                isSaveInProgress,
                config
            }: IVtoShadeInfoRightContainer) => {
                const { seeAllShadesLabel, seeBestShadesLabel, seePairDetailsLabel } = translations;

                const { vtoFeature } = config;
                const vtoFoundationFeature = hasVtoFoundationFeature(vtoFeature);
                const showMatchButtons = vtoFoundationFeature?.showMatchButtons;
                const showPairProduct = hasPairProduct(isPairShadesTabActive, vtoFeature);
                const isSaveMatchEnabled = hasSaveMatch(vtoFeature);
                const isShadeToggleButtonVisible =
                    vtoFeature?.foundation?.isShadeToggleButtonVisible ?? true;

                const seeAllShades = () => {
                    togglePerfectShades && togglePerfectShades(false);
                    setActiveTab(foundationTabs.allShadesTab);
                };

                const seePerfectShades = () => {
                    togglePerfectShades && togglePerfectShades(true);
                    setActiveTab(foundationTabs.perfectShadesTab);
                };
                const showSaveMatchButton =
                    (isSaveMatchEnabled && isPerfectShadeEnabled && !showPairProduct) ||
                    showMatchButtons;

                return (
                    <Wrapper className={shadeInfoRightContainerClassNames}>
                        {!isSaveMatchEnabled && isShadeToggleButtonVisible && (
                            <VtoShadesButton
                                className={seeAllShadesClassName}
                                data-test-id={VTO_ALL_SHADES_BUTTON}
                                isPerfectShadeEnabled={!isPerfectShadeEnabled}
                                showPairProduct={showPairProduct}
                                label={seeAllShadesLabel}
                                onClick={seeAllShades}
                            />
                        )}
                        {isShadeToggleButtonVisible && (
                            <VtoShadesButton
                                className={seePerfectShadesClassName}
                                data-test-id={VTO_PERFECT_SHADES_BUTTON}
                                isPerfectShadeEnabled={isPerfectShadeEnabled}
                                showPairProduct={showPairProduct}
                                label={seeBestShadesLabel}
                                onClick={seePerfectShades}
                            />
                        )}
                        {showPairProduct && (
                            <a
                                className={vtoPairProductLinkClassName}
                                data-test-id={VTO_PAIR_PRODUCT_LINK}
                                href={pairProductUrl}
                            >
                                {seePairDetailsLabel}
                            </a>
                        )}
                        {showSaveMatchButton && (
                            <VtoSaveMatch
                                saveMatches={saveMatches}
                                removeMatches={removeMatches}
                                isMatchSaved={isMatchSaved}
                                isSaveInProgress={isSaveInProgress}
                                isAllShadesTabActive={isAllShadesTabActive}
                            />
                        )}
                    </Wrapper>
                );
            }
        )
    )
);
